import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tooltip
} from '@mui/material'
import { ArrowUpward, ArrowDownward, Favorite, Share, Pause, PlayArrow, VolumeUp, VolumeOff, GridView, List } from '@mui/icons-material/'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import InfiniteScroll from 'react-infinite-scroll-component'
import { isEmpty } from 'lodash'

import { ShareModal } from '../../components/ShareModal'
import { CardInfoShorts } from '../../components/CardInfoShorts'
import { Icons } from '../../components/Icons'
import iconArrowDown from '../../assets/icons/arrowDown.svg'
import iconArrowBack from '../../assets/icons/arrowBack.svg'
import { SET_LIKE_VOD } from '../../redux/actions'
import megaGo from '../../assets/icons/megaGo.svg'
import PlayerVerticalComponent from '../../components/player/PlayerVerticalComponent'

export default function PlayerShortsContainer(props) {
  const {
    serie: {
      imagesSite,
      resume,
      seasons,
      _id,
      isShort,
      preferredPlan
    },
    currentSeason,
    setCurrentSeason,
    isUnlocked,
    useImageCDN,
    mediaId
  } = props
  
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { keycloak } = useSelector(state => state.auth)
  const { currentProfile } = useSelector(state => state.profiles)
  const { data: mediaData } = useSelector(state => state.media)

  const array = useMemo(() => seasons?.[currentSeason]?.vods || [], [seasons, currentSeason])
  const paginateNumber = 20
  // Tamaño máximo del array
  const maxArrayLength = array.length
  const [minArray, setMinArray] = useState(0)
  const [maxArray, setMaxArray] = useState(paginateNumber)
  const [hasMoreItems, setHasMoreItems] = useState(true)
  // Función para dividir el array
  const sliceArray = (first, last, season) => {
    return seasons?.[season]?.vods?.slice(first, last) || []
  }

  const [items, setItems] = useState(sliceArray(minArray, maxArray, currentSeason))
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Al cambiarse el item se actualiza al siguiente array
  useEffect(() => {
    setMinArray(prevMinArray => prevMinArray + paginateNumber)
    setMaxArray(prevMaxArray => prevMaxArray + paginateNumber)
  }, [items])

  const fetchMoreData = () => {
    let nextArray = sliceArray(minArray, maxArray, currentSeason)

    if (items.length >= maxArrayLength) {
      setHasMoreItems(false)
      return
    }
    setItems(items.concat(nextArray))
  }

  const handleSeasonLazy = (event) => {
    setCurrentSeason(event.target.value)
    setMinArray(0)
    setMaxArray(paginateNumber)
    setItems(sliceArray(0, paginateNumber, event.target.value))
    setHasMoreItems(true)
  }

  useEffect(() => {
    setMinArray(0)
    setMaxArray(paginateNumber)
    setHasMoreItems(true)
    setItems(sliceArray(0, paginateNumber, currentSeason))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons])

  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true); // Shorts suelen empezar en play
  const savedVolume = sessionStorage.getItem("megaGoCurrentVolume");
  const [isMuted, setIsMuted] = useState(savedVolume !== null ? JSON.parse(savedVolume) === 0 : false);
  const [activeEpisode, setActiveEpisode] = useState(mediaId);
  const scrollTimeout = useRef(null);

  // Sincronizar el estado local cuando cambia el mediaId por prop (ej: click en lista)
  useEffect(() => {
    setActiveEpisode(mediaId);
  }, [mediaId]);
  
  const handlePlayStateChange = useCallback((playing) => {
    setIsPlaying(playing);
  }, []);

  const handleMuteStateChange = useCallback((muted) => {
    setIsMuted(muted);
  }, []);

  const togglePlay = () => {
    if (playerRef.current && typeof playerRef.current.handlePlayPause === 'function') {
      playerRef.current.handlePlayPause();
    }
  };

  const toggleMute = () => {
    if (playerRef.current && typeof playerRef.current.handleMute === 'function') {
      playerRef.current.handleMute();
    }
  };

  // Functions when go back button is pressed TODO: guardar CW en player actual
  const handleOnClose = () => {
    navigate(`/serie/${_id}`, { replace: true });
  }

  // Calculamos el índice actual y si estamos en los extremos de la lista
  const currentIndex = array.findIndex(vod => vod._id === activeEpisode);
  const isFirstEpisode = currentIndex <= 0;
  const isLastEpisode = currentIndex === -1 || currentIndex === array.length - 1;

  const handleNextEpisode = useCallback(() => {
    if (currentIndex !== -1 && !isLastEpisode) {
      const nextVod = array[currentIndex + 1];
      setActiveEpisode(nextVod._id);
      navigate(`/player/shorts/${nextVod._id}`, { replace: true });
    }
  }, [currentIndex, isLastEpisode, array, navigate]);

  const handlePrevEpisode = useCallback(() => {
    if (currentIndex !== -1 && !isFirstEpisode) {
      const prevVod = array[currentIndex - 1];
      setActiveEpisode(prevVod._id);
      navigate(`/player/shorts/${prevVod._id}`, { replace: true });
    }
  }, [currentIndex, isFirstEpisode, array, navigate]);

  // Manejo de navegación por teclado (Flechas Arriba/Abajo)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (scrollTimeout.current) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleNextEpisode();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrevEpisode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNextEpisode, handlePrevEpisode]);

  // Manejo de Scroll sobre el video
  const handleVideoWheel = useCallback((e) => {
    if (scrollTimeout.current) return;

    if (Math.abs(e.deltaY) > 40) {
      if (e.deltaY > 0) {
        handleNextEpisode();
      
      } else {
        handlePrevEpisode();
      }

      scrollTimeout.current = setTimeout(() => {
        scrollTimeout.current = null;
      }, 700);
    }
  }, [handleNextEpisode, handlePrevEpisode]);

  const [isGridMode, setIsGridMode ] = useState(true)
  
  const handleGridMode = () => {
    if (isGridMode) {
      setIsGridMode(false)
    } else {
      setIsGridMode(true)
    }
  }

  const handleLike = () => {
    dispatch(SET_LIKE_VOD({
      keycloak,
      idCurrentProfile: currentProfile.id,
      idVod: activeEpisode,
      hasLike: !mediaData?.hasLike,
      duration: mediaData?.duration || 0,
      position: mediaData?.position || 0,
      idSerie: _id
    }))
  }

  const handleShareModal = () => {
    setIsModalOpen(true)
  }

  return (
    <main className='appWrap' onWheel={handleVideoWheel}>
      <div className='verticalPlayerPage'>
        <Button
          variant='contained'
          className='shortBackButton'
          onClick={handleOnClose}
          startIcon={<Icons path={iconArrowBack} />}
        >
          Volver
        </Button>
        <Icons path={megaGo} className='shortIcon' />

        <div className='vPlayerColumns'>
        {/* Controles laterales (Desktop) */}
        <div className="lateralButtons navigationButtons">
          <button onClick={handlePrevEpisode} disabled={isFirstEpisode}>
            <ArrowUpward size={24} />
          </button>
          {/* <Link prefetch="intent" to={`/show/${_id}/player/short/${array[currentIndex - 1]?._id}`}>Anterior</Link> */}

          <button onClick={handleNextEpisode} disabled={isLastEpisode}>
            <ArrowDownward size={24} />
          </button>
          {/* <Link prefetch="render" to={`/show/${_id}/player/short/${array[currentIndex + 1]?._id}`}>Siguiente</Link> */}
        </div>

        <section className='vPlayerScroller'>
          {/* Player */}
          <div className='vPlayerContainer'>
            <PlayerVerticalComponent
              mediaId={activeEpisode} 
              ref={playerRef} 
              onPlayStateChange={handlePlayStateChange}
              onMuteStateChange={handleMuteStateChange}
            />
          </div>
          {/* <div className='vPlayerContainer'>
            <PlayerVerticalComponent
              mediaId={activeEpisode} 
              ref={playerRef} 
              onPlayStateChange={handlePlayStateChange}
              onMuteStateChange={handleMuteStateChange}
            />
          </div> */}
        </section>
        {/* Columna de Acciones */}
        <div className="lateralButtons">
          <ActionButton 
            onClick={handleLike} 
            icon={mediaData?.hasLike ? <Favorite style={{ color: '#e36161' }} /> : <Favorite />} 
            label="Me gusta" 
          />
          <ActionButton onClick={handleShareModal} icon={<Share />} label="Compartir" />
          <ActionButton onClick={togglePlay} icon={isPlaying ? <Pause /> : <PlayArrow />} label={isPlaying ? "Pausa" : "Play"} />
          <ActionButton onClick={toggleMute} icon={isMuted ? <VolumeOff /> : <VolumeUp />} label={isMuted ? "Activar sonido" : "Silenciar"} />
        </div>

        {/* Show Info */}
        <section className='vPlayerShowInfo'> 
          {/* Show */}
          <section className='showInfoHeader'>
            <div className='serieData'>
              <div className='serieLogo'>
                <img
                  src={!isEmpty(imagesSite?.logo)
                    ? useImageCDN
                      ? imagesSite.logo.replace('https://api-megago.megamedia.cl', 'https://megago.cdn.mdstrm.com')
                      : imagesSite.logo
                    : 'https://images2-mega.cdn.mdstrm.com/ott/placeholders-imgs/placeholder-logo.png'
                  }
                  alt=''
                  className='serieLogo'
                  onClick={() => navigate(`/serie/${_id}`, { replace: true })}
                />
              </div>
        
              <div className='serieDescription'>
                <p>{resume}</p>
              </div>
            </div>
          </section>

          {/* Episodes */}
          <section>
            <div className="rowS btnWrap btnWrapShorts">
              <header>
                <h2>Episodios</h2>
              </header>
              {seasons?.length > 1 &&
                <FormControl>
                  <Select
                    labelId='season-select'
                    id='season-select'
                    value={currentSeason}
                    onChange={handleSeasonLazy}
                    defaultValue={0}
                    className='selectBase'
                    IconComponent={() => (<Icons path={iconArrowDown} />)}
                  >
                    {seasons.map((season, index) => (
                      <MenuItem value={index} key={index}>{season.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
              <Tooltip title='Cambiar vista de episodios' placement='top'>
                <IconButton 
                  onClick={handleGridMode}
                  className=''>
                  {isGridMode ? <GridView /> : <List />}
                </IconButton>
              </Tooltip>
            </div>
            <div 
              id='scrollableList' 
              className='vPlayerEpisodes'
              onWheel={(e) => e.stopPropagation()}
            >
              <InfiniteScroll
                dataLength={items.length < array.length ? items.length : 10}
                className={isGridMode ? 'grid grid-5-cols cardBox' : 'grid grid-1-cols cardLandscape'}
                next={fetchMoreData}
                hasMore={hasMoreItems}
                scrollThreshold={0.4}
                scrollableTarget='scrollableList'
              >
                {items.map(vod => (
                  <li key={vod._id}>
                    <CardInfoShorts
                      vod={vod}
                      isUnlocked={isUnlocked}
                      pay={vod.pay}
                      showSeason={vod.serie?.seasons?.length > 1 || false}
                      useImageCDN={useImageCDN}
                      isShort={isShort}
                      showId={_id}
                      isPlayingShort={vod._id === mediaId && true}
                      isGridMode={isGridMode}
                      preferredPlan={preferredPlan}
                    />
                  </li>
                ))}
              </InfiniteScroll>
            </div>
          </section>
        </section>
         {/* End Show Info */}
        </div>
      </div>
      <ShareModal setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} />
    </main>
  )
};

const ActionButton = ({ icon, label, onClick }) => (
  <div className='buttonsWithTooltip'>
    <button onClick={onClick}>
      <div>
        {React.cloneElement(icon, { size: 22 })}
      </div>
    </button>
    <span>{label}</span>
  </div>
);